import { LAN_IP_TIMEOUT, UA_DATA_TIMEOUT } from './const';
import type { AttributionOsType, IAttributionDevice } from './types';

interface IUaDataValues {
  platformVersion?: string;
  model?: string;
}

interface INavigatorUaData {
  getHighEntropyValues?: (hints: string[]) => Promise<IUaDataValues>;
}

/** 给 Promise 加超时兜底，超时返回 fallback，避免阻塞上报 */
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise
      .then((value) => resolve(value))
      .catch(() => resolve(fallback))
      .finally(() => clearTimeout(timer));
  });
}

/** iPadOS 13+ 默认伪装成 Mac UA，需要结合触点数判断 */
function isIOSDevice(ua: string): boolean {
  return /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function getOs(ua = navigator.userAgent): AttributionOsType {
  if (/OpenHarmony|HarmonyOS/i.test(ua)) return 'harmony';
  if (isIOSDevice(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  return 'other';
}

/**
 * iOS 26+ 的 Safari 把 UA 中 `OS xx_x` 冻结为 18.x，真实系统版本只体现在 `Version/xx`；
 * CriOS、微信等内置 WebView 的 `OS xx_x` 仍是真实值，且通常没有 Version/。
 * 规则：Version 主版本大于 OS 主版本时，判定 OS 段被冻结，改用 Version。
 */
function parseIOSVersion(ua: string): string {
  const osMatch = ua.match(/OS (\d+(?:_\d+)*) like Mac OS X/i);
  const osVersion = osMatch ? osMatch[1].replace(/_/g, '.') : '';
  const verMatch = ua.match(/Version\/(\d+(?:\.\d+)*)/);
  const safariVersion = verMatch ? verMatch[1] : '';
  if (!osVersion) return safariVersion;
  if (safariVersion && parseInt(safariVersion, 10) > parseInt(osVersion, 10)) return safariVersion;
  return osVersion;
}

/** 从 UA 解析系统版本，统一成 17.5 这种点分格式 */
export function parseOsVersion(os: AttributionOsType, ua = navigator.userAgent): string {
  if (os === 'ios') return parseIOSVersion(ua);
  let match: RegExpMatchArray | null = null;
  if (os === 'harmony') match = ua.match(/(?:OpenHarmony|HarmonyOS)[ /]?(\d+(?:\.\d+)*)/i);
  else if (os === 'android') match = ua.match(/Android (\d+(?:\.\d+)*)/i);
  else if (os === 'windows') match = ua.match(/Windows NT (\d+(?:\.\d+)*)/i);
  else if (os === 'mac') match = ua.match(/Mac OS X (\d+(?:[._]\d+)*)/i);
  return match ? match[1].replace(/_/g, '.') : '';
}

/** 从 Android UA 解析机型；UA 精简后的占位值 K 视为取不到 */
export function parseModel(os: AttributionOsType, ua = navigator.userAgent): string {
  if (os !== 'android') return '';
  const match = ua.match(/Android [^;)]*;\s*(?:[a-z]{2}[-_][a-z]{2};\s*)?([^;)]+?)(?:\s+Build\/|;|\))/i);
  const model = match ? match[1].trim() : '';
  return model === 'K' ? '' : model;
}

/**
 * Chromium 精简 UA 后 Android 版本和机型被冻结（Android 10; K），
 * 支持 userAgentData 时用高熵值覆盖
 */
async function getUaDataValues(): Promise<IUaDataValues> {
  const uaData = (navigator as Navigator & { userAgentData?: INavigatorUaData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return {};
  return withTimeout(uaData.getHighEntropyValues(['platformVersion', 'model']), UA_DATA_TIMEOUT, {});
}

/** 屏幕尺寸按短边/长边归一，避免横竖屏导致和 App 端取值不一致 */
function getScreenSize(): { width: number; height: number; physicalWidth: number; physicalHeight: number } {
  const w = Math.round(window.screen?.width || 0);
  const h = Math.round(window.screen?.height || 0);
  const dpr = window.devicePixelRatio || 1;
  // 物理像素用 Math.round 避免 DPR 小数（如 2.75）导致的浮点误差
  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  return {
    width: Math.min(w, h),
    height: Math.max(w, h),
    physicalWidth: Math.min(pw, ph),
    physicalHeight: Math.max(pw, ph),
  };
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** 过滤掉无意义的地址：0.0.0.0、回环、链路本地、组播 */
function isUsableIPv4(ip: string): boolean {
  if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) return false;
  if (ip === '0.0.0.0' || ip.startsWith('127.') || ip.startsWith('169.254.')) return false;
  const first = Number(ip.split('.')[0]);
  return first < 224;
}

/**
 * 通过 WebRTC ICE candidate 获取内网 IPv4。
 * 现代浏览器多数已用 mDNS（xxx.local）替换真实地址，取不到属于正常情况。
 */
export function getLanIp(): Promise<string> {
  const RTCPeer =
    window.RTCPeerConnection ||
    (window as any).webkitRTCPeerConnection ||
    (window as any).mozRTCPeerConnection;
  if (!RTCPeer) return Promise.resolve('');

  return new Promise((resolve) => {
    let pc: RTCPeerConnection | null = null;
    let done = false;
    const finish = (ip: string) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        pc?.close();
      } catch {
        // ignore
      }
      resolve(ip);
    };
    const timer = setTimeout(() => finish(''), LAN_IP_TIMEOUT);

    try {
      pc = new RTCPeer({ iceServers: [] });
      pc.onicecandidate = (event) => {
        const candidate = event.candidate?.candidate;
        if (!candidate) return;
        // candidate:<foundation> <component> <protocol> <priority> <ip> <port> typ <type>
        const parts = candidate.split(' ');
        const ip = parts[4];
        if (parts[7] === 'host' && ip && isUsableIPv4(ip)) finish(ip);
      };
      pc.createDataChannel('dk');
      pc.createOffer()
        .then((offer) => pc?.setLocalDescription(offer))
        .catch(() => finish(''));
    } catch {
      finish('');
    }
  });
}

/** 采集归因设备参数，单项失败只置空，不影响整体 */
export async function collectDevice(collectLanIp = true): Promise<IAttributionDevice> {
  const ua = navigator.userAgent || '';
  const os = getOs(ua);
  const { width, height, physicalWidth, physicalHeight } = getScreenSize();
  // iOS 上 WebRTC 拿不到内网 IP 且会多一次权限相关开销，直接跳过
  const [uaData, lanIp] = await Promise.all([
    getUaDataValues(),
    collectLanIp && os !== 'ios' ? getLanIp() : Promise.resolve('')
  ]);

  return {
    os,
    os_version: (os === 'android' && uaData.platformVersion) || parseOsVersion(os, ua),
    model: (os === 'android' && uaData.model) || parseModel(os, ua),
    screen_width: width,
    screen_height: height,
    pixel_ratio: Number((window.devicePixelRatio || 1).toFixed(2)),
    physical_width: physicalWidth,
    physical_height: physicalHeight,
    timezone: getTimezone(),
    language: navigator.language || '',
    lan_ip: lanIp,
    user_agent: ua
  };
}
